import { reactive, ref, shallowRef, toRaw } from 'vue'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'

/* Stores */
import { useUserStore } from '@/stores/userStore'

import useMoment from '@/composables/useMoment'

import useLogger from '@/services/loggerService'

import DataTransferService from '@/services/dataTransferService'
const dataTransferService = DataTransferService()

export default function useBlProviders(dtService, additionalParams) {

	const { t: $t } = useI18n()

	/* Store */
	const userStore = useUserStore()
	const {
		selectedCompanyId,
	} = storeToRefs(userStore)

	const logger = useLogger()

	const defaultBlFlags = {
		isLoading: false,
		isCreating: false,
		isDeleting: false,
		isUpdating: false,
		isLoadingCard: false,
		isLoadingPopup: false,
		isLoadingPreview: false,
		// isLoaded: false,
	}

	const {
		predefinedRangeToDataTransfer,
	} = useMoment()

	/**
	 *
	 * @param {object} payload query params to request
	 * @param {string} fromKey key name of query params 'from'
	 * @param {string} toKey key name of query params 'to'
	 * @returns {object}
	 */
	const getPreparedQueryForDt = (payload, fromKey = 'from', toKey = 'to') => {
		// console.log('getPreparedQueryForDt', payload)
		let query = {}
		if (payload.__ao__dateRange) {
			query = {
				...toRaw(payload),
				...predefinedRangeToDataTransfer(toRaw(payload).__ao__dateRange, fromKey, toKey),
			}
			delete query.__ao__dateRange
		} else {
			query = {
				...toRaw(payload),
			}
		}
		return query
	}

	const getCurrentVersions = async (overriddenCompanyId) => {
		const companyId = overriddenCompanyId || selectedCompanyId.value
		try {
			const request = await dataTransferService.cache.getCacheHashes({ urlParams: { companyId } })
			// eslint-disable-next-line no-unused-vars
			const versions = Object.fromEntries(request.map(([key, hash, id, companyId]) => [`${key}--${companyId}`, hash]))

			return versions
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error(error)

			return {}
		}
	}

	const showUiErrorNotification = logger.ui.pushErrorNotification

	const params = Object.assign({
		hasParentEntity: false,

		postProcessEntityCallback: null,

		/**
		 * @description определяет необходимость в сохранении сущностей в локальных переменных
		 */
		useLocalRefs: true,

		showErrorNotificationForLoadingAllEntities: false,
		errorNotificationTextForLoadingAllEntities: $t('The data was loaded with an error. Please contact technical support team'),
		showErrorNotificationForCreatingEntity: false,
		errorNotificationTextForCreatingEntity: $t('Unable to add new entry. Contact technical support.'),
		showErrorNotificationForLoadingOneEntity: false,
		errorNotificationTextForLoadingOneEntity: $t('The data was loaded with an error. Please contact technical support team'),
		showErrorNotificationForUpdatingEntity: false,
		errorNotificationTextForUpdatingEntity: null,
		showErrorNotificationForDeletingEntity: false,
		errorNotificationTextForDeletingEntity: $t('Couldn`t remove. Please contact technical support team.'),

		getAllMethodName: 'getEntities',
		getAllExtendedMethodName: 'getExtendedEntities',
		getAllBasedMethodName: 'getBasedEntities',
		createMethodName: 'createEntity',
		getOneMethodName: 'getEntity',
		getOnePreviewMethodName: 'getEntityPreview',
		getOnePopupMethodName: 'getEntityPopup',
		patchMethodName: 'patchEntity',
		updateMethodName: 'putEntity',
		deleteMethodName: 'deleteEntity',

		/**
		 * @description можно указать дополнительные флаги загрузки со значениями по умолчанию
		 */
		additionalLoadingFlags: {},
	}, additionalParams)

	const loadEntityFlags = reactive({
		...defaultBlFlags,
		isLoaded: false,
		isLoadedCard: false,
		isLoadedPopup: false,
		isLoadedPreview: false,

		isLoadingHashedEntities: false,
		isLoadingHashedBasedEntities: false,
		isLoadingHashedExtendedEntities: false,

		...params.additionalLoadingFlags,
	})

	const entities = ref([])
	/**
	 * Получает все сущности через указанный метод dtService.
	 *
	 * @param {...any} args - Аргументы метода:
	 *   - Если `params.hasParentEntity` === `false`:
	 *     0. `query` (object) - Дополнительные параметры запроса.
	 *     1. `extraParams` (object) - Дополнительные параметры метода.
	 *   - Если `params.hasParentEntity` === `true`:
	 *     0. `parentId` (number) - ID родительской сущности.
	 *     1. `query` (object) - Дополнительные параметры запроса.
	 *     2. `extraParams` (object) - Дополнительные параметры метода.
	 *
	 * @returns {Promise<object[]>} Promise с массивом сущностей.
	 *
	 * @throws {Error} Если не указан модуль `dtService`.
	 * @throws {Error} Если не указано или не является функцией имя метода `getAllMethodName`.
	 */
	const getEntities = (...args) => {
		if (!dtService) {
			throw new Error('Не указан модуль dtService')
		}
		if (!params.getAllMethodName || typeof dtService[params.getAllMethodName] !== 'function') {
			throw new Error('Не указано имя метода dt-модуля (getAllMethodName) для получения всех сущностей')
		}

		loadEntityFlags.isLoading = true

		let _parentId = null
		let _query = null

		let _extraParams = {}

		if (params.hasParentEntity) {
			[_parentId, _query, _extraParams = {}] = args
		} else {
			[_query, _extraParams = {}] = args
		}

		const selfParams = Object.assign(params, _extraParams)

		return new Promise((resolve, reject) => {
			const dtMethodArgs = [selectedCompanyId.value]

			let preparedQuery
			if (_query) {
				preparedQuery = getPreparedQueryForDt(_query, 'time_from', 'time_to')
			}

			if (params.hasParentEntity) {
				dtMethodArgs.push(_parentId, preparedQuery)
			} else {
				dtMethodArgs.push(preparedQuery)
			}

			dtService[params.getAllMethodName](...dtMethodArgs)
				.then((res) => {
					const _entities = params.postProcessEntityCallback && typeof params.postProcessEntityCallback === 'function'
						? res.map((el) => (params.postProcessEntityCallback(el)))
						: res

					if (selfParams.useLocalRefs) {
						entities.value = _entities
					}

					loadEntityFlags.isLoading = false

					resolve(_entities)
				})
				.catch((err) => {
					loadEntityFlags.isLoading = false

					if (params.showErrorNotificationForLoadingAllEntities) {
						logger.ui.pushErrorNotification({
							timeout: 3000,
							text: params.errorNotificationTextForLoadingAllEntities,
						})
					}

					reject(err)
				})
		})
	}

	const hashedEntities = shallowRef({})
	/**
	 * Получает все кешируемые сущности через указанный метод dtService и возвращает их в виде хеш-таблицы.
	 *
	 * @param {...any} args - Аргументы метода:
	 *   - Если `params.hasParentEntity` === `false`:
	 *     0. `query` (object) - Дополнительные параметры запроса.
	 *     1. `extraParams` (object) - Дополнительные параметры метода.
	 *   - Если `params.hasParentEntity` === `true`:
	 *     0. `parentId` (number) - ID родительской сущности.
	 *     1. `query` (object) - Дополнительные параметры запроса.
	 *     2. `extraParams` (object) - Дополнительные параметры метода.
	 *
	 * @returns {Promise<object[]>} Promise с массивом сущностей.
	 *
	 * @throws {Error} Если не указан модуль `dtService`.
	 * @throws {Error} Если не указано или не является функцией имя метода `getAllMethodName`.
	 */
	const getHashedEntities = (...args) => {
		if (!dtService) {
			throw new Error('Не указан модуль dtService')
		}
		if (!params.getAllBasedMethodName || typeof dtService[params.getAllBasedMethodName] !== 'function') {
			throw new Error('Не указано имя метода dt-модуля (getAllBasedMethodName) для получения всех сущностей')
		}

		let _extraParams = args[params.hasParentEntity ? 2 : 1] ?? {}
		const selfParams = Object.assign(params, _extraParams)

		return new Promise((resolve, reject) => {
			loadEntityFlags.isLoadingHashedEntities = true
			getEntities(...args)
				.then((res) => {
					const _hashedEntities = res.reduce((acc, obj) => {
						acc[obj.id] = obj
						return acc
					}, {})

					if (selfParams.useLocalRefs) {
						hashedEntities.value = _hashedEntities
					}

					loadEntityFlags.isLoadingHashedEntities = false
					resolve(_hashedEntities)
				})
				.catch((err) => {
					loadEntityFlags.isLoadingHashedEntities = false
					reject(err)
				})
		})
	}

	const extendedEntities = ref([])
	/**
	 * Получает все кешируемые сущности через указанный метод dtService.
	 *
	 * @param {...any} args - Аргументы метода:
	 *   - Если `params.hasParentEntity` === `false`:
	 *     0. `query` (object) - Дополнительные параметры запроса.
	 *     1. `extraParams` (object) - Дополнительные параметры метода.
	 *   - Если `params.hasParentEntity` === `true`:
	 *     0. `parentId` (number) - ID родительской сущности.
	 *     1. `query` (object) - Дополнительные параметры запроса.
	 *     2. `extraParams` (object) - Дополнительные параметры метода.
	 *
	 * @returns {Promise<object[]>} Promise с массивом сущностей.
	 *
	 * @throws {Error} Если не указан модуль `dtService`.
	 * @throws {Error} Если не указано или не является функцией имя метода `getAllExtendedMethodName`.
	 */
	const getExtendedEntities = (...args) => {
		if (!dtService) {
			throw new Error('Не указан модуль dtService')
		}
		if (!params.getAllExtendedMethodName || typeof dtService[params.getAllExtendedMethodName] !== 'function') {
			throw new Error('Не указано имя метода dt-модуля (getAllExtendedMethodName) для получения всех сущностей')
		}

		loadEntityFlags.isLoading = true

		let _parentId = null
		let _query = null

		let _extraParams = {}

		if (params.hasParentEntity) {
			[_parentId, _query, _extraParams = {}] = args
		} else {
			[_query, _extraParams = {}] = args
		}

		const selfParams = Object.assign(params, _extraParams)

		return new Promise((resolve, reject) => {
			const dtMethodArgs = [selectedCompanyId.value]

			let preparedQuery
			if (_query) {
				preparedQuery = getPreparedQueryForDt(_query, 'time_from', 'time_to')
			}

			if (params.hasParentEntity) {
				dtMethodArgs.push(_parentId, preparedQuery)
			} else {
				dtMethodArgs.push(preparedQuery)
			}

			dtService[params.getAllExtendedMethodName](...dtMethodArgs)
				.then((res) => {
					const _entities = params.postProcessEntityCallback && typeof params.postProcessEntityCallback === 'function'
						? res.map((el) => (params.postProcessEntityCallback(el)))
						: res

					if (selfParams.useLocalRefs) {
						extendedEntities.value = _entities
					}

					loadEntityFlags.isLoading = false

					resolve(_entities)
				})
				.catch((err) => {
					loadEntityFlags.isLoading = false

					if (params.showErrorNotificationForLoadingAllEntities) {
						logger.ui.pushErrorNotification({
							timeout: 3000,
							text: params.errorNotificationTextForLoadingAllEntities,
						})
					}

					reject(err)
				})
		})
	}

	const hashedExtendedEntities = shallowRef({})
	/**
	 * Получает все кешируемые сущности через указанный метод dtService и возвращает их в виде хеш-таблицы.
	 *
	 * @param {...any} args - Аргументы метода:
	 *   - Если `params.hasParentEntity` === `false`:
	 *     0. `query` (object) - Дополнительные параметры запроса.
	 *     1. `extraParams` (object) - Дополнительные параметры метода.
	 *   - Если `params.hasParentEntity` === `true`:
	 *     0. `parentId` (number) - ID родительской сущности.
	 *     1. `query` (object) - Дополнительные параметры запроса.
	 *     2. `extraParams` (object) - Дополнительные параметры метода.
	 *
	 * @returns {Promise<object[]>} Promise с массивом сущностей.
	 *
	 * @throws {Error} Если не указан модуль `dtService`.
	 * @throws {Error} Если не указано или не является функцией имя метода `getAllMethodName`.
	 */
	const getHashedExtendedEntities = (...args) => {
		if (!dtService) {
			throw new Error('Не указан модуль dtService')
		}
		if (!params.getAllBasedMethodName || typeof dtService[params.getAllBasedMethodName] !== 'function') {
			throw new Error('Не указано имя метода dt-модуля (getAllBasedMethodName) для получения всех сущностей')
		}

		let _extraParams = args[params.hasParentEntity ? 2 : 1] ?? {}
		const selfParams = Object.assign(params, _extraParams)

		return new Promise((resolve, reject) => {
			loadEntityFlags.isLoadingHashedExtendedEntities = true
			getExtendedEntities(...args)
				.then((res) => {
					const _hashedExtendedEntities = res.reduce((acc, obj) => {
						acc[obj.id] = obj
						return acc
					}, {})

					if (selfParams.useLocalRefs) {
						hashedExtendedEntities.value = _hashedExtendedEntities
					}

					loadEntityFlags.isLoadingHashedExtendedEntities = false
					resolve(_hashedExtendedEntities)
				})
				.catch((err) => {
					loadEntityFlags.isLoadingHashedExtendedEntities = false
					reject(err)
				})
		})
	}

	const basedEntities = ref([])
	/**
	 * Получает все кешируемые сущности через указанный метод dtService.
	 *
	 * @param {...any} args - Аргументы метода:
	 *   - Если `params.hasParentEntity` === `false`:
	 *     0. `query` (object) - Дополнительные параметры запроса.
	 *     1. `extraParams` (object) - Дополнительные параметры метода.
	 *   - Если `params.hasParentEntity` === `true`:
	 *     0. `parentId` (number) - ID родительской сущности.
	 *     1. `query` (object) - Дополнительные параметры запроса.
	 *     2. `extraParams` (object) - Дополнительные параметры метода.
	 *
	 * @returns {Promise<object[]>} Promise с массивом сущностей.
	 *
	 * @throws {Error} Если не указан модуль `dtService`.
	 * @throws {Error} Если не указано или не является функцией имя метода `getAllBasedMethodName`.
	 */
	const getBasedEntities = (...args) => {
		if (!dtService) {
			throw new Error('Не указан модуль dtService')
		}
		if (!params.getAllBasedMethodName || typeof dtService[params.getAllBasedMethodName] !== 'function') {
			throw new Error('Не указано имя метода dt-модуля (getAllBasedMethodName) для получения всех сущностей')
		}

		loadEntityFlags.isLoading = true

		let _parentId = null
		let _query = null

		let _extraParams = {}

		if (params.hasParentEntity) {
			[_parentId, _query, _extraParams = {}] = args
		} else {
			[_query, _extraParams = {}] = args
		}

		const selfParams = Object.assign(params, _extraParams)

		return new Promise((resolve, reject) => {
			const dtMethodArgs = [selectedCompanyId.value]

			let preparedQuery
			if (_query) {
				preparedQuery = getPreparedQueryForDt(_query, 'time_from', 'time_to')
			}

			if (params.hasParentEntity) {
				dtMethodArgs.push(_parentId, preparedQuery)
			} else {
				dtMethodArgs.push(preparedQuery)
			}

			dtService[params.getAllBasedMethodName](...dtMethodArgs)
				.then((res) => {
					const _entities = params.postProcessEntityCallback && typeof params.postProcessEntityCallback === 'function'
						? res.map((el) => (params.postProcessEntityCallback(el)))
						: res

					if (selfParams.useLocalRefs) {
						basedEntities.value = _entities
					}

					loadEntityFlags.isLoading = false

					resolve(_entities)
				})
				.catch((err) => {
					loadEntityFlags.isLoading = false

					if (params.showErrorNotificationForLoadingAllEntities) {
						logger.ui.pushErrorNotification({
							timeout: 3000,
							text: params.errorNotificationTextForLoadingAllEntities,
						})
					}

					reject(err)
				})
		})
	}

	const hashedBasedEntities = shallowRef({})
	/**
	 * Получает все кешируемые сущности через указанный метод dtService и возвращает их в виде хеш-таблицы.
	 *
	 * @param {...any} args - Аргументы метода:
	 *   - Если `params.hasParentEntity` === `false`:
	 *     0. `query` (object) - Дополнительные параметры запроса.
	 *     1. `extraParams` (object) - Дополнительные параметры метода.
	 *   - Если `params.hasParentEntity` === `true`:
	 *     0. `parentId` (number) - ID родительской сущности.
	 *     1. `query` (object) - Дополнительные параметры запроса.
	 *     2. `extraParams` (object) - Дополнительные параметры метода.
	 *
	 * @returns {Promise<object[]>} Promise с массивом сущностей.
	 *
	 * @throws {Error} Если не указан модуль `dtService`.
	 * @throws {Error} Если не указано или не является функцией имя метода `getAllMethodName`.
	 */
	const getHashedBasedEntities = (...args) => {
		if (!dtService) {
			throw new Error('Не указан модуль dtService')
		}
		if (!params.getAllBasedMethodName || typeof dtService[params.getAllBasedMethodName] !== 'function') {
			throw new Error('Не указано имя метода dt-модуля (getAllBasedMethodName) для получения всех сущностей')
		}

		let _extraParams = args[params.hasParentEntity ? 2 : 1] ?? {}
		const selfParams = Object.assign(params, _extraParams)

		return new Promise((resolve, reject) => {
			loadEntityFlags.isLoadingHashedBasedEntities = true
			getBasedEntities(...args)
				.then((res) => {
					const _hashedBasedEntities = res.reduce((acc, obj) => {
						acc[obj.id] = obj
						return acc
					}, {})

					if (selfParams.useLocalRefs) {
						hashedBasedEntities.value = _hashedBasedEntities
					}

					loadEntityFlags.isLoadingHashedBasedEntities = false
					resolve(_hashedBasedEntities)
				})
				.catch((err) => {
					loadEntityFlags.isLoadingHashedBasedEntities = false
					reject(err)
				})
		})
	}

	/**
	 * Создает сущность через указанный метод dtService.
	 *
	 * @param {...any} args - Аргументы метода:
	 *   - Если `params.hasParentEntity` === `false`:
	 *     0. `body` (object) - Дополнительные параметры запроса.
	 *   - Если `params.hasParentEntity` === `true`:
	 *     0. `parentId` (number) - ID родительской сущности.
	 *     1. `body` (object) - Дополнительные параметры запроса.
	 *
	 * @returns {Promise<object>} Promise с данными созданной сущности.
	 *
	 * @throws {Error} Если не указан модуль `dtService`.
	 * @throws {Error} Если не указано или не является функцией имя метода `createMethodName`.
	 */
	const createEntity = (...args) => {
		if (!dtService) {
			throw new Error('Не указан модуль dtService')
		}
		if (!params.createMethodName || typeof dtService[params.createMethodName] !== 'function') {
			throw new Error('Не указано имя метода dt-модуля (createMethodName) для получения всех сущностей')
		}

		loadEntityFlags.isCreating = true

		let _parentId = null
		let _body = null

		if (params.hasParentEntity) {
			[_parentId, _body] = args
		} else {
			[_body] = args
		}

		return new Promise((resolve, reject) => {
			const dtMethodArgs = [selectedCompanyId.value]

			if (params.hasParentEntity) {
				dtMethodArgs.push(_parentId, _body)
			} else {
				dtMethodArgs.push(_body)
			}

			dtService[params.createMethodName](...dtMethodArgs)
				.then((res) => {
					loadEntityFlags.isCreating = false

					resolve(res)
				})
				.catch((err) => {
					loadEntityFlags.isCreating = false

					if (params.showErrorNotificationForCreatingEntity && params.errorNotificationTextForCreatingEntity) {
						logger.ui.pushErrorNotification({
							timeout: 3000,
							text: params.errorNotificationTextForCreatingEntity,
						})
					}

					reject(err)
				})
		})
	}

	const entity = ref(null)
	/**
	 * Получает сущность через указанный метод dtService.
	 *
	 * @param {...any} args - Аргументы метода:
	 *   - Если `params.hasParentEntity` === `false`:
	 *     0. `entityId` (number) - ID сущности.
	 *     1. `query` (object) - Дополнительные параметры запроса.
	 *     2. `extraParams` (object) - Дополнительные параметры метода.
	 *   - Если `params.hasParentEntity` === `true`:
	 *     0. `parentId` (number) - ID родительской сущности.
	 *     1. `entityId` (number) - ID сущности.
	 *     2. `query` (object) - Дополнительные параметры запроса.
	 *     3. `extraParams` (object) - Дополнительные параметры метода.
	 *
	 * @returns {Promise<object>} Promise с данными сущности.
	 *
	 * @throws {Error} Если не указан модуль `dtService`.
	 * @throws {Error} Если не указано или не является функцией имя метода `getOneMethodName`.
	 */
	const getEntity = (...args) => {
		if (!dtService) {
			throw new Error('Не указан модуль dtService')
		}
		if (!params.getOneMethodName || typeof dtService[params.getOneMethodName] !== 'function') {
			throw new Error('Не указано имя метода dt-модуля (getOneMethodName) для получения всех сущностей')
		}

		loadEntityFlags.isLoadingCard = true

		let _parentId = null
		let _entityId = null
		let _query = null
		let _extraParams = {}

		if (params.hasParentEntity) {
			[_parentId, _entityId, _query, _extraParams = {}] = args
		} else {
			[_entityId, _query, _extraParams = {}] = args
		}

		const selfParams = Object.assign(params, _extraParams)

		return new Promise((resolve, reject) => {
			const dtMethodArgs = [selectedCompanyId.value]

			if (params.hasParentEntity) {
				dtMethodArgs.push(_parentId, _entityId, _query)
			} else {
				dtMethodArgs.push(_entityId, _query)
			}

			dtService[params.getOneMethodName](...dtMethodArgs)
				.then((res) => {
					const _entity = params.postProcessEntityCallback && typeof params.postProcessEntityCallback === 'function'
						? params.postProcessEntityCallback(res)
						: res

					if (selfParams.useLocalRefs) {
						entity.value = _entity
					}

					loadEntityFlags.isLoadingCard = false
					loadEntityFlags.isLoadedCard = true

					resolve(_entity)
				})
				.catch((err) => {
					loadEntityFlags.isLoadingCard = false

					if (params.showErrorNotificationForLoadingOneEntity) {
						logger.ui.pushErrorNotification({
							timeout: 3000,
							text: params.errorNotificationTextForLoadingOneEntity,
						})
					}

					reject(err)
				})
		})
	}

	const entityPreview = ref(null)
	/**
	 * Получает превью сущности через указанный метод dtService.
	 *
	 * @param {...any} args - Аргументы метода:
	 *   - Если `params.hasParentEntity` === `false`:
	 *     0. `entityId` (number) - ID сущности.
	 *     1. `query` (object) - Дополнительные параметры запроса.
	 *     2. `extraParams` (object) - Дополнительные параметры метода.
	 *   - Если `params.hasParentEntity` === `true`:
	 *     0. `parentId` (number) - ID родительской сущности.
	 *     1. `entityId` (number) - ID сущности.
	 *     2. `query` (object) - Дополнительные параметры запроса.
	 *     3. `extraParams` (object) - Дополнительные параметры метода.
	 *
	 * @returns {Promise<object>} Promise с данными сущности.
	 *
	 * @throws {Error} Если не указан модуль `dtService`.
	 * @throws {Error} Если не указано или не является функцией имя метода `getOnePreviewMethodName`.
	 */
	const getEntityPreview = (...args) => {
		if (!dtService) {
			throw new Error('Не указан модуль dtService')
		}
		if (!params.getOnePreviewMethodName || typeof dtService[params.getOnePreviewMethodName] !== 'function') {
			throw new Error('Не указано имя метода dt-модуля (getOnePreviewMethodName) для получения всех сущностей')
		}

		loadEntityFlags.isLoadingPreview = true

		let _parentId = null
		let _entityId = null
		let _query = null
		let _extraParams = {}

		if (params.hasParentEntity) {
			[_parentId, _entityId, _query, _extraParams = {}] = args
		} else {
			[_entityId, _query, _extraParams = {}] = args
		}

		const selfParams = Object.assign(params, _extraParams)

		return new Promise((resolve, reject) => {
			const dtMethodArgs = [selectedCompanyId.value]

			if (params.hasParentEntity) {
				dtMethodArgs.push(_parentId, _entityId, _query)
			} else {
				dtMethodArgs.push(_entityId, _query)
			}

			dtService[params.getOnePreviewMethodName](...dtMethodArgs)
				.then((res) => {
					const _entity = params.postProcessEntityCallback && typeof params.postProcessEntityCallback === 'function'
						? params.postProcessEntityCallback(res)
						: res

					if (selfParams.useLocalRefs) {
						entityPreview.value = _entity
					}

					loadEntityFlags.isLoadingPreview = false
					loadEntityFlags.isLoadedPreview = true

					resolve(_entity)
				})
				.catch((err) => {
					loadEntityFlags.isLoadingPreview = false

					if (params.showErrorNotificationForLoadingOneEntity) {
						logger.ui.pushErrorNotification({
							timeout: 3000,
							text: params.errorNotificationTextForLoadingOneEntity,
						})
					}

					reject(err)
				})
		})
	}

	const entityPopup = ref(null)
	/**
	 * Получает Popup сущности через указанный метод dtService.
	 *
	 * @param {...any} args - Аргументы метода:
	 *   - Если `params.hasParentEntity` === `false`:
	 *     0. `entityId` (number) - ID сущности.
	 *     1. `query` (object) - Дополнительные параметры запроса.
	 *     2. `extraParams` (object) - Дополнительные параметры метода.
	 *   - Если `params.hasParentEntity` === `true`:
	 *     0. `parentId` (number) - ID родительской сущности.
	 *     1. `entityId` (number) - ID сущности.
	 *     2. `query` (object) - Дополнительные параметры запроса.
	 *     3. `extraParams` (object) - Дополнительные параметры метода.
	 *
	 * @returns {Promise<object>} Promise с данными сущности.
	 *
	 * @throws {Error} Если не указан модуль `dtService`.
	 * @throws {Error} Если не указано или не является функцией имя метода `getOnePopupMethodName`.
	 */
	const getEntityPopup = (...args) => {
		if (!dtService) {
			throw new Error('Не указан модуль dtService')
		}
		if (!params.getOnePopupMethodName || typeof dtService[params.getOnePopupMethodName] !== 'function') {
			throw new Error('Не указано имя метода dt-модуля (getOnePopupMethodName) для получения всех сущностей')
		}

		loadEntityFlags.isLoadingPopup = true

		let _parentId = null
		let _entityId = null
		let _query = null
		let _extraParams = {}

		if (params.hasParentEntity) {
			[_parentId, _entityId, _query, _extraParams = {}] = args
		} else {
			[_entityId, _query, _extraParams = {}] = args
		}

		const selfParams = Object.assign(params, _extraParams)

		return new Promise((resolve, reject) => {
			const dtMethodArgs = [selectedCompanyId.value]

			if (params.hasParentEntity) {
				dtMethodArgs.push(_parentId, _entityId, _query)
			} else {
				dtMethodArgs.push(_entityId, _query)
			}

			dtService[params.getOnePopupMethodName](...dtMethodArgs)
				.then((res) => {
					const _entity = params.postProcessEntityCallback && typeof params.postProcessEntityCallback === 'function'
						? params.postProcessEntityCallback(res)
						: res

					if (selfParams.useLocalRefs) {
						entityPopup.value = _entity
					}

					loadEntityFlags.isLoadingPopup = false
					loadEntityFlags.isLoadedPopup = true

					resolve(_entity)
				})
				.catch((err) => {
					loadEntityFlags.isLoadingPopup = false

					if (params.showErrorNotificationForLoadingOneEntity) {
						logger.ui.pushErrorNotification({
							timeout: 3000,
							text: params.errorNotificationTextForLoadingOneEntity,
						})
					}

					reject(err)
				})
		})
	}

	/**
	 * Патч сущности через указанный метод dtService.
	 *
	 * @param {...any} args - Аргументы метода:
	 *   - Если `params.hasParentEntity` === `false`:
	 *     0. `entityId` (number) - ID сущности.
	 *     1. `body` (object) - Дополнительные параметры запроса.
	 *   - Если `params.hasParentEntity` === `true`:
	 *     0. `parentId` (number) - ID родительской сущности.
	 *     1. `entityId` (number) - ID сущности.
	 *     2. `body` (object) - Дополнительные параметры запроса.
	 *
	 * @returns {Promise<object>} Promise с данными редактируемой сущности.
	 *
	 * @throws {Error} Если не указан модуль `dtService`.
	 * @throws {Error} Если не указано или не является функцией имя метода `patchMethodName`.
	 */
	const patchEntity = (...args) => {
		if (!dtService) {
			throw new Error('Не указан модуль dtService')
		}
		if (!params.patchMethodName || typeof dtService[params.patchMethodName] !== 'function') {
			throw new Error('Не указано имя метода dt-модуля (patchMethodName) для получения всех сущностей')
		}

		loadEntityFlags.isUpdating = true

		let _parentId = null
		let _entityId = null
		let _body = null

		if (params.hasParentEntity) {
			[_parentId, _entityId, _body] = args
		} else {
			[_entityId, _body] = args
		}

		return new Promise((resolve, reject) => {
			const dtMethodArgs = [selectedCompanyId.value]

			if (params.hasParentEntity) {
				dtMethodArgs.push(_parentId, _entityId, _body)
			} else {
				dtMethodArgs.push(_entityId, _body)
			}

			dtService[params.patchMethodName](...dtMethodArgs)
				.then((res) => {
					const _entity = params.postProcessEntityCallback && typeof params.postProcessEntityCallback === 'function'
						? params.postProcessEntityCallback(res)
						: res

					loadEntityFlags.isUpdating = false

					resolve(_entity)
				})
				.catch((err) => {
					loadEntityFlags.isUpdating = false

					if (params.showErrorNotificationForUpdatingEntity && params.errorNotificationTextForUpdatingEntity) {
						logger.ui.pushErrorNotification({
							timeout: 3000,
							text: params.errorNotificationTextForUpdatingEntity,
						})
					}

					reject(err)
				})
		})
	}

	/**
	 * Обновление всей сущности через указанный метод dtService.
	 *
	 * @param {...any} args - Аргументы метода:
	 *   - Если `params.hasParentEntity` === `false`:
	 *     0. `entityId` (number) - ID сущности.
	 *     1. `body` (object) - Дополнительные параметры запроса.
	 *   - Если `params.hasParentEntity` === `true`:
	 *     0. `parentId` (number) - ID родительской сущности.
	 *     1. `entityId` (number) - ID сущности.
	 *     2. `body` (object) - Дополнительные параметры запроса.
	 *
	 * @returns {Promise<object>} Promise с данными редактируемой сущности.
	 *
	 * @throws {Error} Если не указан модуль `dtService`.
	 * @throws {Error} Если не указано или не является функцией имя метода `updateMethodName`.
	 */
	const updateEntity = (...args) => {
		if (!dtService) {
			throw new Error('Не указан модуль dtService')
		}
		if (!params.updateMethodName || typeof dtService[params.updateMethodName] !== 'function') {
			throw new Error('Не указано имя метода dt-модуля (updateMethodName) для получения всех сущностей')
		}

		loadEntityFlags.isUpdating = true

		let _parentId = null
		let _entityId = null
		let _body = null

		if (params.hasParentEntity) {
			[_parentId, _entityId, _body] = args
		} else {
			[_entityId, _body] = args
		}

		return new Promise((resolve, reject) => {
			const dtMethodArgs = [selectedCompanyId.value]

			if (params.hasParentEntity) {
				dtMethodArgs.push(_parentId, _entityId, _body)
			} else {
				dtMethodArgs.push(_entityId, _body)
			}

			dtService[params.updateMethodName](...dtMethodArgs)
				.then((res) => {
					const _entity = params.postProcessEntityCallback && typeof params.postProcessEntityCallback === 'function'
						? params.postProcessEntityCallback(res)
						: res

					loadEntityFlags.isUpdating = false

					resolve(_entity)
				})
				.catch((err) => {
					loadEntityFlags.isUpdating = false

					if (params.showErrorNotificationForUpdatingEntity && params.errorNotificationTextForUpdatingEntity) {
						logger.ui.pushErrorNotification({
							timeout: 3000,
							text: params.errorNotificationTextForUpdatingEntity,
						})
					}

					reject(err)
				})
		})
	}

	/**
	 * Удаление сущности через указанный метод dtService.
	 *
	 * @param {...any} args - Аргументы метода:
	 *   - Если `params.hasParentEntity` === `false`:
	 *     0. `entityId` (number) - ID сущности.
	 *   - Если `params.hasParentEntity` === `true`:
	 *     0. `parentId` (number) - ID родительской сущности.
	 *     1. `entityId` (number) - ID сущности.
	 *
	 * @returns {Promise<any>} Promise с ответом сервера.
	 *
	 * @throws {Error} Если не указан модуль `dtService`.
	 * @throws {Error} Если не указано или не является функцией имя метода `deleteMethodName`.
	 */
	const deleteEntity = (...args) => {
		if (!dtService) {
			throw new Error('Не указан модуль dtService')
		}
		if (!params.deleteMethodName || typeof dtService[params.deleteMethodName] !== 'function') {
			throw new Error('Не указано имя метода dt-модуля (deleteMethodName) для получения всех сущностей')
		}

		loadEntityFlags.isDeleting = true

		let _parentId = null
		let _entityId = null

		if (params.hasParentEntity) {
			[_parentId, _entityId] = args
		} else {
			[_entityId] = args
		}

		return new Promise((resolve, reject) => {
			const dtMethodArgs = [selectedCompanyId.value]

			if (params.hasParentEntity) {
				dtMethodArgs.push(_parentId, _entityId)
			} else {
				dtMethodArgs.push(_entityId)
			}

			dtService[params.deleteMethodName](...dtMethodArgs)
				.then((res) => {
					loadEntityFlags.isDeleting = false

					resolve(res)
				})
				.catch((err) => {
					loadEntityFlags.isDeleting = false

					// TODO: добавить логику обработки ошибок связанных с отсутствием прав

					if (params.showErrorNotificationForDeletingEntity && params.errorNotificationTextForDeletingEntity) {
						logger.ui.pushErrorNotification({
							timeout: 3000,
							text: params.errorNotificationTextForDeletingEntity,
						})
					}

					reject(err)
				})
		})
	}

	return {
		selectedCompanyId,

		defaultBlFlags,

		getPreparedQueryForDt,
		getCurrentVersions,

		showUiErrorNotification,

		loadEntityFlags,

		entities,
		getEntities,

		hashedEntities,
		getHashedEntities,

		extendedEntities,
		getExtendedEntities,

		hashedExtendedEntities,
		getHashedExtendedEntities,

		basedEntities,
		getBasedEntities,

		hashedBasedEntities,
		getHashedBasedEntities,

		createEntity,

		entity,
		getEntity,

		entityPreview,
		getEntityPreview,

		entityPopup,
		getEntityPopup,

		patchEntity,
		updateEntity,
		deleteEntity,
	}

}
